import React, { useEffect, useRef, useState, useContext } from "react";
import {
  Box,
  Card,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
  ViewColumn as ColumnIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  InfoOutlined,
  CheckCircle as ApproveIcon,
} from "@mui/icons-material";
import "tabulator-tables/dist/css/tabulator.min.css";
import "../styles/tabulator.css";
import * as XLSX from "xlsx";
import PendingChangesTable from "./PendingChangesTable.js";
import ChangeHistoryDialog from "./ChangeHistoryDialog.js";
import ColumnSelectorDialog from "./ColumnSelectorDialog.js";
import AfeService from "../service/afeService.js";
import LevelCapexService from "../service/levelCapexService.js";
import { UserContext } from "../context/UserContext.js";
import AfeSearchBox from "./AfeSearchBox.js";
import { useNavigate } from "react-router-dom";

const AfeTable = () => {
  // Funzione utility per normalizzare i valori per un confronto affidabile
  const normalizeValue = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim().toUpperCase();
  };

  // Formatter combinato per gestire sia i valori approvati che quelli pending
  const combinedFormatter = function (cell) {
    const rowData = cell.getRow().getData();
    const field = cell.getField();
    const value = cell.getValue();

    // CASO 1: Se il record è approvato, mostra il valore approvato
    if (rowData.recentlyApproved) {
      // Per utenti admin, aggiungi un indicatore speciale se il campo è stato modificato dopo l'approvazione
      if (
        (bestRole === "Admin" || bestRole === "Validator") &&
        rowData.hasPendingChanges &&
        rowData.pendingValues &&
        rowData.pendingValues[field] !== undefined
      ) {
        const pendingValue = rowData.pendingValues[field];

        // Se il valore pending è diverso dal valore attuale
        if (String(pendingValue) !== String(value)) {
          // Crea un elemento div per contenere il valore con indicatore speciale
          const container = document.createElement("div");
          container.style.position = "relative";
          container.style.width = "100%";
          container.style.display = "flex";
          container.style.alignItems = "center";

          // Valore con indicatore di modifica admin
          const valueSpan = document.createElement("span");
          valueSpan.textContent =
            value !== null && value !== undefined ? value : "";
          valueSpan.style.color = "#2E7D32"; // Verde scuro
          valueSpan.style.fontWeight = "bold";
          valueSpan.style.position = "relative";

          // Indicatore speciale admin (stella in alto a destra)
          const adminIndicator = document.createElement("div");
          adminIndicator.style.position = "absolute";
          adminIndicator.style.top = "0";
          adminIndicator.style.right = "2px";
          adminIndicator.style.width = "0";
          adminIndicator.style.height = "0";
          adminIndicator.style.borderLeft = "6px solid transparent";
          adminIndicator.style.borderTop = "6px solid #3F51B5"; // Blu per admin

          // Tooltip per dettagli
          container.title = `Valore approvato che verrà modificato da ${rowData.pendingChangeBy} (${bestRole})`;

          container.appendChild(valueSpan);
          container.appendChild(adminIndicator);

          return container;
        }
      }

      return value !== null && value !== undefined ? value : "";
    }

    // CASO 2: Se il record ha un valore pending per questa cella
    if (
      rowData.hasPendingChanges &&
      rowData.pendingValues &&
      rowData.pendingValues[field] !== undefined
    ) {
      const pendingValue = rowData.pendingValues[field];

      // Se il valore pending è diverso dal valore attuale
      if (String(pendingValue) !== String(value)) {
        // Crea un elemento div per contenere sia il valore attuale che quello pending
        const container = document.createElement("div");
        container.style.position = "relative";
        container.style.width = "100%";
        container.style.display = "flex";
        container.style.alignItems = "center";

        // Valore attuale (barrato)
        const currentValueSpan = document.createElement("span");
        currentValueSpan.textContent =
          value !== null && value !== undefined ? value : "-";
        currentValueSpan.style.textDecoration = "line-through";
        currentValueSpan.style.color = "#999";
        currentValueSpan.style.marginRight = "8px";

        // Valore pending (in evidenza)
        const pendingValueSpan = document.createElement("span");
        pendingValueSpan.textContent =
          pendingValue !== null && pendingValue !== undefined
            ? pendingValue
            : "-";
        pendingValueSpan.style.color =
          rowData.pendingChangeBy === user.email ? "#0D47A1" : "#B71C1C";
        pendingValueSpan.style.fontWeight = "bold";

        // Indicatore di modifica (triangolino giallo)
        const indicator = document.createElement("div");
        indicator.style.position = "absolute";
        indicator.style.top = "0";
        indicator.style.right = "2px";
        indicator.style.width = "0";
        indicator.style.height = "0";
        indicator.style.borderLeft = "6px solid transparent";
        indicator.style.borderTop = "6px solid #FFC107";

        // Tooltip per dettagli sulla modifica
        container.title = `Valore originale: ${value || "-"}\nValore pending: ${
          pendingValue || "-"
        }\nModificato da: ${rowData.pendingChangeBy}`;

        container.appendChild(currentValueSpan);
        container.appendChild(pendingValueSpan);
        container.appendChild(indicator);

        return container;
      }
    }

    // CASO 3: Valore normale (nessuna modifica pending e non approvato)
    return value !== null && value !== undefined ? value : "";
  };
  const [columnHideDefault, setColumnHideDefault] = useState(['responsible_editable', 'userResponsible_editable', 'companyCode_frozen', 'ou_frozen']);

  const columns = [
    {
      formatter: "rowSelection",
      titleFormatter: "rowSelection",
      titleFormatterParams: { rowRange: "all" },
      hozAlign: "center",
      headerSort: false,
      width: 30,
      frozen: true,
      visible: true,
      cssClass: "frozen-column",
      columnType: "selection",
      cellClick: function (e, cell) {
        const row = cell.getRow();

        if (row.isSelected()) {
          row.deselect();
        } else {
          row.select();
        }
      },
    },
    {
      title: "AFE Code",
      field: "afeCode",
      widthGrow: 1,
      editor: "input",
      sorter: "string",
      validator: "required",
      editable: false,
      headerFilter: "input",
      frozen: true,
      visible: !columnHideDefault.includes("afeCode_frozen"),
      cssClass: "frozen-column",
      columnType: "frozen",
      columnId: "afeCode_frozen",
    },
    {
      title: "Description",
      field: "afeDesc",
      widthGrow: 2,
      editor: "input",
      sorter: "string",
      validator: "required",
      editable: false,
      headerFilter: "input",
      frozen: true,
      visible: !columnHideDefault.includes("afeDesc_frozen"),
      cssClass: "frozen-column",
      columnType: "frozen",
      columnId: "afeDesc_frozen",
    },
    {
      title: "Fiscal Year",
      field: "afeFyscalYear",
      widthGrow: 1,
      editor: "input",
      sorter: "string",
      validator: "required",
      editable: false,
      frozen: true,
      visible: !columnHideDefault.includes("afeFyscalYear_frozen"),
      headerFilter: "input",
      cssClass: "frozen-column",
      columnType: "frozen",
      columnId: "afeFyscalYear_frozen",
    },
    {
      title: "Closure Year",
      field: "fiscalYearClosure",
      widthGrow: 1,
      editor: "input",
      sorter: "string",
      validator: "required",
      editable: false,
      frozen: true,
      visible: !columnHideDefault.includes("fiscalYearClosure_frozen"),
      headerFilter: "input",
      cssClass: "frozen-column",
      columnType: "frozen",
      columnId: "fiscalYearClosure_frozen",
    },
    {
      title: "Fiscal Year Record",
      field: "fiscalYearRecord",
      widthGrow: 1,
      editor: "input",
      sorter: "string",
      validator: "required",
      editable: false,
      frozen: true,
      visible: !columnHideDefault.includes("fiscalYearRecord_frozen"),
      headerFilter: "input",
      cssClass: "frozen-column",
      columnType: "frozen",
      columnId: "fiscalYearRecord_frozen",
    },
    {
      title: "SAP_Responsible",
      field: "sapResponsibleOriginal",
      widthGrow: 1,
      editor: "input",
      sorter: "string",
      validator: "required",
      editable: false,
      frozen: true,
      visible: !columnHideDefault.includes("sapResponsibleOriginal_frozen"),
      headerFilter: "input",
      cssClass: "frozen-column",
      columnType: "frozen",
      columnId: "sapResponsibleOriginal_frozen",
    },
    {
      title: "Responsible",
      field: "responsible",
      widthGrow: 1,
      editor: "input",
      formatter: combinedFormatter,
      sorter: "string",
      validator: "required",
      editable: function (cell) {
        const rowData = cell.getRow().getData();
        return (
          bestRole !== "Coordinator" &&
          (bestRole === "Admin" ||
            bestRole === "Validator" ||
            !rowData.recentlyApproved) && // Admin e Validator possono modificare anche record approvati
          (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
        );
      },
      frozen: false,
      visible: !columnHideDefault.includes("responsible_editable"),
      headerFilter: "input",
      cssClass: "editable-column",
      columnType: "editable",
      columnId: "responsible_editable",
    },
    {
      title: "Program ID",
      field: "programId",
      widthGrow: 1,
      editor: "input",
      sorter: "string",
      validator: "required",
      editable: false,
      frozen: true,
      visible: !columnHideDefault.includes("programId_frozen"),
      headerFilter: "input",
      cssClass: "frozen-column",
      columnType: "frozen",
      columnId: "programId_frozen",
    },
    {
      title: "SAP_UserResponsible",
      field: "sapUserResponsibleOriginal",
      widthGrow: 1,
      editor: "input",
      sorter: "string",
      validator: "required",
      editable: false,
      frozen: true,
      visible: !columnHideDefault.includes("sapUserResponsibleOriginal_frozen"),
      headerFilter: "input",
      cssClass: "frozen-column",
      columnType: "frozen",
      columnId: "sapUserResponsibleOriginal_frozen",
    },
    {
      title: "UserResponsible",
      field: "userResponsible",
      widthGrow: 1,
      editor: "input",
      formatter: combinedFormatter,
      sorter: "string",
      validator: "required",
      editable: function (cell) {
        const rowData = cell.getRow().getData();
        return (
          bestRole !== "Coordinator" &&
          (bestRole === "Admin" ||
            bestRole === "Validator" ||
            !rowData.recentlyApproved) && // Admin e Validator possono modificare anche record approvati
          (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
        );
      },
      frozen: false,
      visible: !columnHideDefault.includes("userResponsible_editable"),
      headerFilter: "input",
      cssClass: "editable-column",
      columnType: "editable",
      columnId: "userResponsible_editable",
    },
    {
      title: "Company Code",
      field: "companyCode",
      widthGrow: 1,
      editor: "input",
      sorter: "string",
      validator: "required",
      editable: false,
      frozen: true,
      visible: !columnHideDefault.includes("companyCode_frozen"),
      headerFilter: "input",
      cssClass: "frozen-column",
      columnType: "frozen",
      columnId: "companyCode_frozen",
    },
    {
      title: "OU",
      field: "ou",
      widthGrow: 1,
      editor: "input",
      sorter: "string",
      validator: "required",
      editable: false,
      frozen: true,
      visible: !columnHideDefault.includes("ou_frozen"),
      headerFilter: "input",
      cssClass: "frozen-column",
      columnType: "frozen",
      columnId: "ou_frozen",
    },
    {
      title: "Local/KPI Approval",
      field: "localKPI",
      widthGrow: 1,
      editor: "input",
      sorter: "string",
      validator: "required",
      editable: false,
      frozen: true,
      visible: !columnHideDefault.includes("localKPI_frozen"),
      headerFilter: "input",
      cssClass: "frozen-column",
      columnType: "frozen",
      columnId: "localKPI_frozen",
    },
    {
      title: "Category",
      field: "catAfe",
      widthGrow: 1,
      editor: "input",
      sorter: "string",
      validator: "required",
      editable: false,
      frozen: true,
      visible: !columnHideDefault.includes("catAfe_frozen"),
      headerFilter: "input",
      cssClass: "frozen-column",
      columnType: "frozen",
      columnId: "catAfe_frozen",
    },
    {
      title: "Total Afe",
      field: "totalAfe",
      widthGrow: 1,
      editor: "input",
      sorter: "number",
      validator: ["required", "numeric"],
      formatter: "money",
      formatterParams: {
        symbol: "€",
        precision: 2,
        thousand: ".",
        decimal: ",",
      },
      editable: false,
      frozen: true,
      headerFilter: "input",
      cssClass: "frozen-column",
      visible: !columnHideDefault.includes("totalAfe_frozen"),
      columnType: "frozen",
      columnId: "totalAfe_frozen",
    },
    {
      title: "Capex Year",
      field: "capexYear",
      widthGrow: 1,
      editor: "input",
      sorter: "number",
      validator: ["required", "numeric"],
      formatter: "money",
      formatterParams: {
        symbol: "€",
        precision: 2,
        thousand: ".",
        decimal: ",",
      },
      editable: false,
      frozen: true,
      headerFilter: "input",
      cssClass: "frozen-column",
      visible: !columnHideDefault.includes("capexYear_frozen"),
      columnType: "frozen",
      columnId: "capexYear_frozen",
    },
    {
      title: "Speso Progressivo",
      field: "spesoProgressivo",
      widthGrow: 1,
      editor: "input",
      sorter: "number",
      headerSort: true,
      validator: ["required", "numeric"],
      formatter: "money",
      formatterParams: {
        symbol: "€",
        precision: 2,
        thousand: ".",
        decimal: ",",
      },
      editable: false,
      frozen: true,
      headerFilter: "input",
      cssClass: "frozen-column",
      visible: !columnHideDefault.includes("spesoProgressivo_frozen"),
      columnType: "frozen",
      columnId: "spesoProgressivo_frozen",
    },
    {
      title: "Local/Corporate",
      field: "localCorporate",
      widthGrow: 1,
      editor: "list",
      formatter: combinedFormatter, // Uso del formatter combinato
      editorParams: {
        values: ["Local", "Corporate"],
      },
      editable: function (cell) {
        const rowData = cell.getRow().getData();
        return (
          bestRole !== "Coordinator" &&
          (bestRole === "Admin" ||
            bestRole === "Validator" ||
            !rowData.recentlyApproved) && // Admin e Validator possono modificare anche record approvati
          (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
        );
      },
      headerFilter: "list",
      headerFilterParams: {
        values: ["", "Local", "Corporate"],
      },
      cssClass: "editable-column",
      visible: !columnHideDefault.includes("localCorporate_editable"),
      columnType: "editable",
      columnId: "localCorporate_editable",
    },
    {
      title: "Raggruppamento 1",
      field: "raggruppamento1",
      widthGrow: 1,
      editor: "list",
      formatter: combinedFormatter, // Uso del formatter combinato
      headerSort: true,
      editable: function (cell) {
        const rowData = cell.getRow().getData();
        return (
          bestRole !== "Coordinator" &&
          (bestRole === "Admin" ||
            bestRole === "Validator" ||
            !rowData.recentlyApproved) && // Admin e Validator possono modificare anche record approvati
          (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
        );
      },
      editorParams: {
        valuesLookup: function (cell) {
          try {
            const row = cell.getRow();
            const rowData = row.getData();
            const table = cell.getTable();

            // Get localCorporate value (with pending check)
            let localCorporateValue = rowData.localCorporate;
            if (
              rowData.pendingValues &&
              rowData.pendingValues.localCorporate !== undefined
            ) {
              localCorporateValue = rowData.pendingValues.localCorporate;
            }

            // Enhanced check for empty localCorporateValue - don't show options if not set
            if (!localCorporateValue || localCorporateValue.trim() === "") {
              showNotification(
                "Selezionare prima il valore Local/Corporate per visualizzare le opzioni disponibili",
                "info"
              );
              // Return explicitly empty result that won't show anything
              return [{ value: "", label: "" }];
            }

            // If the data isn't loaded, reload it synchronously
            if (!listValuesWithCodes || listValuesWithCodes.length === 0) {

              // Show loading notification
              showNotification("Caricamento dati in corso...", "info");

              // Create a promise that resolves when data is loaded
              return new Promise((resolve) => {
                // Call the existing function that loads level1 data
                LevelCapexService.getLevels1()
                  .then((data) => {

                    // Process the data the same way as in your original code
                    const completeData = data.map((item) => ({
                      code: item.level1_code,
                      desc: item.level1_desc,
                      display: `${item.level1_code} - ${item.level1_desc}`,
                      localCorporate: item.localCorporate,
                    }));

                    // Update the state variable
                    setListValuesWithCodes(completeData);

                    // Filter the options
                    const filteredOptions = completeData
                      .filter(
                        (item) =>
                          String(item.localCorporate || "")
                            .trim()
                            .toUpperCase() ===
                          String(localCorporateValue || "")
                            .trim()
                            .toUpperCase()
                      )
                      .map((item) => ({
                        value: item.desc,
                        label: `${item.code} - ${item.desc}`,
                      }));

                    if (filteredOptions.length === 0) {
                      // No matching options found after loading data
                      showNotification(
                        `Nessuna opzione trovata per '${localCorporateValue}'.`,
                        "warning"
                      );

                      // Return empty set - don't show any options
                      resolve([{ value: "", label: "" }]);
                    } else {
                      resolve(filteredOptions);
                    }
                  })
                  .catch((error) => {
                    showNotification(
                      "Errore nel caricamento dei dati. Riprovare più tardi.",
                      "error"
                    );
                    resolve([{ value: "", label: "Errore di caricamento" }]);
                  });
              });
            }

            // If we have data available, use it directly
            // Filter options with strict comparison
            const filteredOptions = listValuesWithCodes
              .filter((item) => {
                const itemValue = String(item.localCorporate || "")
                  .trim()
                  .toUpperCase();
                const searchValue = String(localCorporateValue || "")
                  .trim()
                  .toUpperCase();
                const matches = itemValue === searchValue;

                return matches;
              })
              .map((item) => ({
                value: item.desc,
                label: `${item.code} - ${item.desc}`,
              }));

            if (filteredOptions.length === 0) {
              // If no Local/Corporate is selected, don't show any options
              if (!localCorporateValue || localCorporateValue.trim() === "") {
                return [{ value: "", label: "" }];
              }

              // No matching options despite having data - this is unexpected
              showNotification(
                `Nessuna opzione trovata per '${localCorporateValue}'.`,
                "warning"
              );

              // Return a single empty option to show nothing in the dropdown
              return [{ value: "", label: "" }];
            }

            return filteredOptions;
          } catch (error) {
            console.error("Error in valuesLookup:", error);
            showNotification(
              "Si è verificato un errore. Riprovare più tardi.",
              "error"
            );
            return [{ value: "", label: "Errore - riprovare" }];
          }
        },
        displayField: "label",
        valueField: "value",
      },
      headerFilter: "list",
      headerFilterParams: {
        values: [], // Sarà popolato nel caricamento dati
        displayField: "label",
        valueField: "value",
      },
      visible: !columnHideDefault.includes("raggruppamento1_editable"),
      cssClass: "editable-column",
      columnType: "editable",
      columnId: "raggruppamento1_editable",
    },
    {
      title: "Raggruppamento 2",
      field: "raggruppamento2",
      widthGrow: 1,
      editor: "list",
      formatter: combinedFormatter,
      headerSort: true,
      editable: function (cell) {
        const rowData = cell.getRow().getData();
        return (
          bestRole !== "Coordinator" &&
          (bestRole === "Admin" ||
            bestRole === "Validator" ||
            !rowData.recentlyApproved) &&
          (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email)
        );
      },
      editorParams: {
        valuesLookup: function (cell) {
          try {
            const row = cell.getRow();
            const rowData = row.getData();

            // Ottieni il valore di raggruppamento1 considerando i pending values
            let raggruppamento1Value = rowData.raggruppamento1;
            if (
              rowData.pendingValues &&
              rowData.pendingValues.raggruppamento1 !== undefined
            ) {
              raggruppamento1Value = rowData.pendingValues.raggruppamento1;
            }

            // Controlla che raggruppamento1 sia impostato
            if (!raggruppamento1Value || raggruppamento1Value.trim() === "") {
              showNotification(
                "Selezionare prima il valore Raggruppamento 1 per visualizzare le opzioni disponibili",
                "info"
              );
              return [{ value: "", label: "" }];
            }

            // Funzione asincrona per caricare le opzioni
            return new Promise(async (resolve) => {
              try {
                // Carica i dati level1 per trovare il codice corretto
                const level1Data = await LevelCapexService.getLevels1();
                const selectedLevel1 = level1Data.find(
                  (item) => item.level1_desc === raggruppamento1Value
                );

                if (!selectedLevel1) {
                  showNotification(
                    `Nessun Level1 trovato per '${raggruppamento1Value}'`,
                    "warning"
                  );
                  resolve([{ value: "", label: "" }]);
                  return;
                }

                // Carica i level2 per questo level1
                const level2Data = await LevelCapexService.getLevels2ByLevel1(
                  selectedLevel1.level1_code
                );

                if (level2Data.length === 0) {
                  showNotification(
                    `Nessuna opzione Level2 trovata per '${raggruppamento1Value}'`,
                    "warning"
                  );
                  resolve([{ value: "", label: "" }]);
                  return;
                }

                const options = level2Data.map((item) => ({
                  value: item.level2_desc,
                  label: `${item.level2_code} - ${item.level2_desc}`,
                }));

                resolve(options);
              } catch (error) {
                console.error(
                  "Errore nel caricamento opzioni raggruppamento2:",
                  error
                );
                showNotification(
                  "Errore nel caricamento delle opzioni. Riprovare più tardi.",
                  "error"
                );
                resolve([{ value: "", label: "Errore di caricamento" }]);
              }
            });
          } catch (error) {
            console.error("Error in raggruppamento2 valuesLookup:", error);
            showNotification(
              "Si è verificato un errore. Riprovare più tardi.",
              "error"
            );
            return [{ value: "", label: "Errore - riprovare" }];
          }
        },
        displayField: "label",
        valueField: "value",
      },
      headerFilter: "list",
      headerFilterParams: {
        values: [],
        displayField: "label",
        valueField: "value",
      },
      visible: !columnHideDefault.includes("raggruppamento2_editable"),
      cssClass: "editable-column",
      columnType: "editable",
      columnId: "raggruppamento2_editable",
    },
    {
      title: "Raggruppamento 3",
      field: "raggruppamento3",
      widthGrow: 1,
      editor: "list",
      formatter: combinedFormatter,
      headerSort: true,
      editable: function (cell) {
        const rowData = cell.getRow().getData();
        return (
          bestRole !== "Coordinator" &&
          (bestRole === "Admin" ||
            bestRole === "Validator" ||
            !rowData.recentlyApproved) &&
          (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email)
        );
      },
      editorParams: {
        valuesLookup: function (cell) {
          try {
            const row = cell.getRow();
            const rowData = row.getData();

            // Ottieni i valori di raggruppamento1 e raggruppamento2 considerando i pending values
            let raggruppamento1Value = rowData.raggruppamento1;
            let raggruppamento2Value = rowData.raggruppamento2;

            if (
              rowData.pendingValues &&
              rowData.pendingValues.raggruppamento1 !== undefined
            ) {
              raggruppamento1Value = rowData.pendingValues.raggruppamento1;
            }

            if (
              rowData.pendingValues &&
              rowData.pendingValues.raggruppamento2 !== undefined
            ) {
              raggruppamento2Value = rowData.pendingValues.raggruppamento2;
            }

            // Controlla che entrambi i livelli superiori siano impostati
            if (!raggruppamento1Value || raggruppamento1Value.trim() === "") {
              showNotification(
                "Selezionare prima il valore Raggruppamento 1 per visualizzare le opzioni disponibili",
                "info"
              );
              return [{ value: "", label: "" }];
            }

            if (!raggruppamento2Value || raggruppamento2Value.trim() === "") {
              showNotification(
                "Selezionare prima il valore Raggruppamento 2 per visualizzare le opzioni disponibili",
                "info"
              );
              return [{ value: "", label: "" }];
            }

            // Funzione asincrona per caricare le opzioni
            return new Promise(async (resolve) => {
              try {
                // Carica i dati level1 per trovare il codice corretto
                const level1Data = await LevelCapexService.getLevels1();
                const selectedLevel1 = level1Data.find(
                  (item) => item.level1_desc === raggruppamento1Value
                );

                if (!selectedLevel1) {
                  showNotification(
                    `Nessun Level1 trovato per '${raggruppamento1Value}'`,
                    "warning"
                  );
                  resolve([{ value: "", label: "" }]);
                  return;
                }

                // Carica i level2 per questo level1
                const level2Data = await LevelCapexService.getLevels2ByLevel1(
                  selectedLevel1.level1_code
                );

                const selectedLevel2 = level2Data.find(
                  (item) => item.level2_desc === raggruppamento2Value
                );

                if (!selectedLevel2) {
                  showNotification(
                    `Nessun Level2 trovato per '${raggruppamento2Value}' in '${raggruppamento1Value}'`,
                    "warning"
                  );
                  resolve([{ value: "", label: "" }]);
                  return;
                }

                // Carica i level3 per questo level2
                const level3Data = await LevelCapexService.getLevels3ByLevel2(
                  selectedLevel2.level2_code
                );

                if (level3Data.length === 0) {
                  showNotification(
                    `Nessuna opzione Level3 trovata per '${raggruppamento2Value}'`,
                    "warning"
                  );
                  resolve([{ value: "", label: "" }]);
                  return;
                }

                const options = level3Data.map((item) => ({
                  value: item.level3_desc,
                  label: `${item.level3_code} - ${item.level3_desc}`,
                }));

                resolve(options);
              } catch (error) {
                console.error(
                  "Errore nel caricamento opzioni raggruppamento3:",
                  error
                );
                showNotification(
                  "Errore nel caricamento delle opzioni. Riprovare più tardi.",
                  "error"
                );
                resolve([{ value: "", label: "Errore di caricamento" }]);
              }
            });
          } catch (error) {
            console.error("Error in raggruppamento3 valuesLookup:", error);
            showNotification(
              "Si è verificato un errore. Riprovare più tardi.",
              "error"
            );
            return [{ value: "", label: "Errore - riprovare" }];
          }
        },
        displayField: "label",
        valueField: "value",
      },
      headerFilter: "list",
      headerFilterParams: {
        values: [],
        displayField: "label",
        valueField: "value",
      },
      visible: !columnHideDefault.includes("raggruppamento3_editable"),
      cssClass: "editable-column",
      columnType: "editable",
      columnId: "raggruppamento3_editable",
    },
  ];
  // Refs
  const tableRef = useRef(null);
 
  const [lastHeight, setLastHeight] = useState(0);
  const [height, setHeight] = useState(0);

  // Stati principali
  const [table, setTable] = useState(null);
  const [listValuesWithCodes, setListValuesWithCodes] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
  });
  const { user, bestRole } = useContext(UserContext);

  // NUOVO: Stato per memorizzare i dati originali
  const [originalTableData, setOriginalTableData] = useState({});

  // Stati per i dialogs
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const [filtersActive, setFiltersActive] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({});
  const [searchBoxVisible, setSearchBoxVisible] = useState(false);
  const navigate = useNavigate();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedRecordsCount, setSelectedRecordsCount] = useState(0);
  const [selectedRecordIds, setSelectedRecordIds] = useState("");

  // Stati per la validazione e lo storico
  const [pendingChangesCount, setPendingChangesCount] = useState(0);
  const [changeHistory, setChangeHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({
    status: "all",
    dateFrom: "",
    dateTo: "",
    field: "",
  });

  const [scrollTopPosition, setScrollTopPosition] = useState(0);

  const [loadingFetch, setLoadingFetch] = useState(false);

  // Funzione per il log di debugging
  const logRecordStatuses = () => {
    if (!table) return;

    const rows = table.getRows();

    let recentlyApprovedCount = 0;
    let pendingChangesCount = 0;

    rows.forEach((row) => {
      const data = row.getData();
      if (data.recentlyApproved === true) {
        recentlyApprovedCount++;
        // Controlla lo stato visivo della riga
        const element = row.getElement();
        const hasClass = element.classList.contains("recently-approved-row");
        const hasBorder = element.style.borderLeft.includes("solid");

        // Controlla lo stato visivo delle celle editabili
        for (const field of [
          "raggruppamento1",
          "raggruppamento2",
          "raggruppamento3",
          "localCorporate",
          "userResponsible",
          "responsible",
        ]) {
          const cell = row.getCell(field);
          if (cell) {
            const value = cell.getValue();
            const element = cell.getElement();
            const hasClass = element.classList.contains("approved-field");
          }
        }
      }

      if (data.hasPendingChanges) {
        pendingChangesCount++;
      }
    });
  };

  // Funzioni helper di base
  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type });
  };

  // Funzione per aggiornare le opzioni dropdown per i valori pending
  const refreshDropdownOptionsForPendingValues = async () => {
    if (!table) return;

    try {
      // Per ogni riga della tabella, controlla se ha valori pending e aggiorna le opzioni di conseguenza
      table.getRows().forEach(async (row) => {
        const rowData = row.getData();

        if (rowData.hasPendingChanges && rowData.pendingValues) {
          const pendingValues = rowData.pendingValues;

          // Se c'è un valore pending per raggruppamento1, aggiorna le opzioni per raggruppamento2
          if (pendingValues.raggruppamento1 !== undefined) {
            try {
              const level1Data = await LevelCapexService.getLevels1();
              const selectedLevel1 = level1Data.find(
                (item) => item.level1_desc === pendingValues.raggruppamento1
              );

              if (selectedLevel1) {
                const level2Data = await LevelCapexService.getLevels2ByLevel1(
                  selectedLevel1.level1_code
                );

                const raggruppamento2Options = level2Data.map((item) => ({
                  value: item.level2_desc,
                  label: `${item.level2_code} - ${item.level2_desc}`,
                }));

                // Aggiorna le opzioni per raggruppamento2
                const raggruppamento2Column =
                  table.getColumn("raggruppamento2");
                if (raggruppamento2Column) {
                  raggruppamento2Column.updateDefinition({
                    headerFilterParams: {
                      values: raggruppamento2Options,
                      displayField: "label",
                      valueField: "value",
                    },
                  });
                }
              }
            } catch (error) {
              console.error(
                "Errore nell'aggiornamento opzioni raggruppamento2:",
                error
              );
            }
          }

          // Se ci sono valori pending per raggruppamento1 e raggruppamento2, aggiorna le opzioni per raggruppamento3
          if (
            pendingValues.raggruppamento1 !== undefined &&
            pendingValues.raggruppamento2 !== undefined
          ) {
            try {
              const level1Data = await LevelCapexService.getLevels1();
              const selectedLevel1 = level1Data.find(
                (item) => item.level1_desc === pendingValues.raggruppamento1
              );

              if (selectedLevel1) {
                const level2Data = await LevelCapexService.getLevels2ByLevel1(
                  selectedLevel1.level1_code
                );

                const selectedLevel2 = level2Data.find(
                  (item) => item.level2_desc === pendingValues.raggruppamento2
                );

                if (selectedLevel2) {
                  const level3Data = await LevelCapexService.getLevels3ByLevel2(
                    selectedLevel2.level2_code
                  );

                  const raggruppamento3Options = level3Data.map((item) => ({
                    value: item.level3_desc,
                    label: `${item.level3_code} - ${item.level3_desc}`,
                  }));

                  // Aggiorna le opzioni per raggruppamento3
                  const raggruppamento3Column =
                    table.getColumn("raggruppamento3");
                  if (raggruppamento3Column) {
                    raggruppamento3Column.updateDefinition({
                      headerFilterParams: {
                        values: raggruppamento3Options,
                        displayField: "label",
                        valueField: "value",
                      },
                    });
                  }
                }
              }
            } catch (error) {
              console.error(
                "Errore nell'aggiornamento opzioni raggruppamento3:",
                error
              );
            }
          }
        }
      });
    } catch (error) {
      console.error(
        "Errore generale nell'aggiornamento opzioni dropdown:",
        error
      );
    }
  };

  // Funzione per caricare i valori pending nei record
  const loadPendingValuesIntoRecords = async () => {
    if (!table) return;

    try {
      // Ottieni tutte le modifiche pending
      const pendingRecordChanges = await AfeService.getPendingRecordChanges();

      // Filtra solo quelle con stato "pending"
      const pendingChanges = pendingRecordChanges.filter(
        (change) => change.status === "pending"
      );

      // Crea una mappa di modifiche pending per ID record
      const pendingRecordsMap = new Map();

      // Elabora ciascuna modifica pending
      pendingChanges.forEach((change) => {
        try {
          // Parse dei JSON delle modifiche
          const originalRecord = JSON.parse(change.originalRecordJson);
          const modifiedRecord = JSON.parse(change.modifiedRecordJson);

          // Trova i campi che sono stati modificati
          const pendingValues = {};
          Object.keys(modifiedRecord).forEach((key) => {
            if (
              JSON.stringify(modifiedRecord[key]) !==
              JSON.stringify(originalRecord[key])
            ) {
              pendingValues[key] = modifiedRecord[key];
            }
          });

          // Salva le informazioni sulle modifiche
          if (change.afeId) {
            pendingRecordsMap.set(change.afeId, {
              modifiedBy: change.modifiedBy,
              pendingValues: pendingValues,
            });
          }
          if (change.afeOriginalId) {
            pendingRecordsMap.set(change.afeOriginalId, {
              modifiedBy: change.modifiedBy,
              pendingValues: pendingValues,
            });
          }
        } catch (error) {
          console.error("Errore nel parsing dei JSON:", error);
        }
      });

      // Aggiorna le righe della tabella con i valori pending
      table.getRows().forEach((row) => {
        const rowData = row.getData();
        const recordId = rowData.id || rowData.original_Id;

        if (recordId && pendingRecordsMap.has(recordId)) {
          const pendingInfo = pendingRecordsMap.get(recordId);

          // Aggiorna il record con le informazioni sulle modifiche pending
          row.update({
            ...rowData,
            hasPendingChanges: true,
            pendingChangeBy: pendingInfo.modifiedBy,
            pendingValues: pendingInfo.pendingValues,
          });

          // Forza il ridisegno delle celle con valori pending per applicare il formatter
          for (const field in pendingInfo.pendingValues) {
            const cell = row.getCell(field);
            if (cell) {
              cell.getElement().classList.add("has-pending-value");
            }
          }
        }
      });

      // Forza un ridisegno della tabella per applicare tutti i formattatori
      table.redraw(true);

      // Aggiorna le opzioni dropdown per i valori pending
      await refreshDropdownOptionsForPendingValues();
    } catch (error) {
      console.error("Errore nel caricamento dei valori pending:", error);
    }
  };

  const fetchData = async () => {
    setLoadingFetch(true);
    try {
      const response = await AfeService.getAfes();
      if (table) { 
        const originalDataMap = {};
        response.forEach((record) => {
          // Normalizza i valori null e undefined in stringhe vuote per i campi editabili
          const normalizedRecord = { ...record };
          [
            "localCorporate",
            "userResponsible",
            "responsible",
            "raggruppamento1",
            "raggruppamento2",
            "raggruppamento3",
          ].forEach((key) => {
            if (
              normalizedRecord[key] === null ||
              normalizedRecord[key] === undefined
            ) {
              normalizedRecord[key] = "";
            }
          });
          originalDataMap[record.id] = normalizedRecord;
        });
        setOriginalTableData(originalDataMap);

        table.setData(response);

        // Carica e applica i valori pending dopo il caricamento dei dati
        await loadPendingValuesIntoRecords();
        setLoadingFetch(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showNotification("Error loading data", "error");
      setLoadingFetch(false);
    }
  };

  const loadPendingChangesCount = async () => {
    try {
      const recordLevelChanges = await AfeService.getPendingRecordChanges();
      setPendingChangesCount(recordLevelChanges.length);
      return recordLevelChanges.length;
    } catch (error) {
      console.error("Error loading pending changes count:", error);
      showNotification("Error loading pending changes count", "error");
      return 0;
    }
  };

  const loadChangeHistory = async () => {
    try {
      const recordLevelHistory = await AfeService.getRecordChangeHistory(
        historyFilters
      );
      setChangeHistory(recordLevelHistory);
    } catch (error) {
      console.error("Error loading change history:", error);
      showNotification("Error loading history", "error");
    }
  };

  const handleDownloadExcel = () => {
    if (table) {
      const data = table.getData();
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "AFE Data");
      XLSX.writeFile(wb, "CAPEX_data.xlsx");
    }
  };
  // const handleDownloadExcel = () => {
  //   if (table) {
  //     // Ottieni le colonne visibili
  //     const visibleColumns = table.getColumns().filter(col => col.isVisible());
  //     const visibleFields = visibleColumns.map(col => col.getField()).filter(Boolean);

  //     // Ottieni i dati della tabella filtrando solo i campi visibili
  //     const data = table.getData().map(row => {
  //       const filteredRow = {};
  //       visibleFields.forEach(field => {
  //         filteredRow[field] = row[field];
  //       });
  //       return filteredRow;
  //     });

  //     const wb = XLSX.utils.book_new();
  //     const ws = XLSX.utils.json_to_sheet(data);
  //     XLSX.utils.book_append_sheet(wb, ws, "AFE Data");
  //     XLSX.writeFile(wb, "CAPEX_data.xlsx");
  //   }
  // };

  const handleColumnVisibilityChange = (columnId, isVisible, field, type) => {
    if (table) {
      const column = table.getColumn(field);
      if (column) {
        isVisible ? column.show() : column.hide();
        setVisibleColumns((prev) => ({ ...prev, [columnId]: isVisible }));
      }
    }
  };

  const handlePendingChangesClick = () => {
    navigate("/capex/pending");
  };

  const handleSearch = ({
    afeCode,
    fiscalYear,
    responsible,
    userResponsible,
  }) => {
    if (!table) return;

    table.clearFilter();

    if (!afeCode && !fiscalYear && !responsible && !userResponsible) {
      return;
    }

    const filters = [];

    if (afeCode) {
      filters.push({
        field: "afeCode",
        type: "like",
        value: afeCode,
      });
    }

    if (fiscalYear) {
      filters.push({
        field: "afeFyscalYear",
        type: "like",
        value: fiscalYear,
      });
    }

    if (responsible) {
      filters.push({
        field: "responsible",
        type: "like",
        value: responsible,
      });
    }

    if (userResponsible) {
      filters.push({
        field: "userResponsible",
        type: "like",
        value: userResponsible,
      });
    }

    if (filters.length > 0) {
      table.setFilter(filters);
      showNotification(
        `Ricerca applicata: ${[
          afeCode ? `AFE Code = ${afeCode}` : "",
          fiscalYear ? `Fiscal Year = ${fiscalYear}` : "",
          responsible ? `Responsible = ${responsible}` : "",
          userResponsible ? `UserResponsible = ${userResponsible}` : "",
        ]
          .filter(Boolean)
          .join(", ")}`,
        "info"
      );
    }
  };

  const handleApproveSelectedRecords = () => {
    if (!table) return;

    // Ottieni le righe selezionate
    const selectedRows = table.getSelectedRows();

    if (selectedRows.length === 0) {
      showNotification("Seleziona almeno un record da approvare", "warning");
      return;
    }

    // Estrai gli ID dei record selezionati e formattali come stringa CSV
    const selectedIds = selectedRows.map((row) => row.getData().id);
    const idsCsv = selectedIds.join(",");

    // Salva i dati e apri la dialog di conferma
    setSelectedRecordsCount(selectedIds.length);
    setSelectedRecordIds(idsCsv);
    setConfirmDialogOpen(true);
  };

  const confirmApproval = async () => {
    try {
      // Chiudi la dialog
      setConfirmDialogOpen(false);

      // Chiama il servizio per approvare i record
      await AfeService.setCapexRecentlyApproved(selectedRecordIds);

      showNotification(
        `${selectedRecordsCount} record approvati con successo`,
        "success"
      );

      // Ricarica i dati della tabella per aggiornare lo stato
      await fetchData();

      // Aggiorna la visualizzazione
      await disableEditingForRecordsWithPendingChanges();
    } catch (error) {
      console.error("Errore nell'approvazione dei record:", error);
      showNotification("Errore durante l'approvazione dei record", "error");
    }
  };

  const disableEditingForRecordsWithPendingChanges = async () => {
    if (!table) return;
    try {
      // Get all pending changes
      const pendingRecordChanges = await AfeService.getPendingRecordChanges();

      // Filter only those with "pending" status
      const pendingChanges = pendingRecordChanges.filter(
        (change) => change.status === "pending"
      );

      // Create a map associating record ID with its modifier and values
      const pendingRecordsMap = new Map();

      pendingChanges.forEach((change) => {
        try {
          // Parse dei JSON delle modifiche
          const originalRecord = JSON.parse(change.originalRecordJson);
          const modifiedRecord = JSON.parse(change.modifiedRecordJson);

          // Trova i campi che sono stati modificati
          const pendingValues = {};
          Object.keys(modifiedRecord).forEach((key) => {
            if (
              JSON.stringify(modifiedRecord[key]) !==
              JSON.stringify(originalRecord[key])
            ) {
              pendingValues[key] = modifiedRecord[key];
            }
          });

          if (change.afeId) {
            pendingRecordsMap.set(change.afeId, {
              modifiedBy: change.modifiedBy,
              pendingValues: pendingValues,
            });
          }
          if (change.afeOriginalId) {
            pendingRecordsMap.set(change.afeOriginalId, {
              modifiedBy: change.modifiedBy,
              pendingValues: pendingValues,
            });
          }
        } catch (error) {
          console.error("Errore nel parsing dei JSON:", error);

          // Fallback al comportamento originale in caso di errore
          if (change.afeId) {
            pendingRecordsMap.set(change.afeId, {
              modifiedBy: change.modifiedBy,
              pendingValues: {},
            });
          }
          if (change.afeOriginalId) {
            pendingRecordsMap.set(change.afeOriginalId, {
              modifiedBy: change.modifiedBy,
              pendingValues: {},
            });
          }
        }
      });

      // Process all rows
      table.getRows().forEach((row) => {
        const rowData = row.getData();
        const recordId = rowData.id || rowData.original_Id;

        // Ottieni le informazioni sulla modifica pending per questo record
        const pendingInfo = pendingRecordsMap.get(recordId);
        const pendingChangeBy = pendingInfo ? pendingInfo.modifiedBy : null;
        const pendingValues = pendingInfo ? pendingInfo.pendingValues : null;

        // Verifica se il record è stato approvato
        const isRecentlyApproved = rowData.recentlyApproved === true;

        // Se il record è stato approvato, applica stile speciale
        if (isRecentlyApproved) {
          // Aggiungi classe e stili alla riga
          const rowElement = row.getElement();
          rowElement.classList.add("recently-approved-row");
          rowElement.style.backgroundColor = "#F1F8E9"; // Verde molto chiaro per la riga
          rowElement.setAttribute("data-approved", "true");

          const cells = rowElement.querySelectorAll(".tabulator-cell");
          if (cells.length > 0) {
            const selectionCell = cells[0]; // La prima cella è la colonna di selezione
            selectionCell.style.backgroundColor = "#2E7D32"; // Verde scuro
            selectionCell.style.color = "#FFFFFF"; // Testo bianco per migliorare contrasto
          }

          // Applica lo stile a ciascuna cella modificabile
          for (const field of [
            "raggruppamento1",
            "raggruppamento2",
            "raggruppamento3",
            "localCorporate",
            "userResponsible",
            "responsible",
          ]) {
            const cell = row.getCell(field);
            if (cell) {
              const cellElement = cell.getElement();

              // Style differently for admin and validator users
              if (bestRole === "Admin" || bestRole === "Validator") {
                // Per gli admin e validator, usa uno stile che indica che è approvato ma modificabile
                cellElement.style.backgroundColor = "#E8F5E9"; // Verde chiaro
                cellElement.style.color = "#2E7D32"; // Verde scuro
                cellElement.style.border = "1px dashed #81C784"; // Bordo tratteggiato per indicare che è modificabile
                cellElement.title = `Questo campo è stato approvato. Come ${bestRole}, puoi comunque modificarlo.`;
                cellElement.classList.add("approved-field-admin-editable");
              } else {
                // Per gli altri utenti, mantieni lo stile originale
                cellElement.style.backgroundColor = "#E8F5E9"; // Verde chiaro
                cellElement.style.color = "#2E7D32"; // Verde scuro
                cellElement.style.border = "1px solid #81C784"; // Bordo verde
                cellElement.title =
                  "Questo campo è stato approvato e non può essere modificato";
                cellElement.classList.add("approved-field");
              }

              // IMPORTANTE: Assicurati che il contenuto della cella sia visibile
              const value = cell.getValue();
              if (value !== null && value !== undefined && value !== "") {
                cellElement.setAttribute("data-value", value);
                cellElement.setAttribute(
                  "aria-label",
                  bestRole === "Admin" || bestRole === "Validator"
                    ? `Campo approvato modificabile: ${value}`
                    : `Campo approvato: ${value}`
                );
              }
            }
          }
        }

        // If we found pending changes for this record
        if (pendingChangeBy) {
          // Flag the record as having pending changes
          row.update(
            {
              hasPendingChanges: true,
              pendingChangeBy: pendingChangeBy,
              pendingValues: pendingValues || {},
            },
            true
          );

          // Highlight the row using direct style instead of classes
          const rowElement = row.getElement();
          rowElement.style.backgroundColor = "#FFF8E1"; // Light yellow background
          rowElement.setAttribute("data-modified-by", pendingChangeBy);

          const cells = rowElement.querySelectorAll(".tabulator-cell");
          if (cells.length > 0) {
            const selectionCell = cells[0];
            selectionCell.style.backgroundColor = "#ffd700"; // Giallo per selezione
            selectionCell.style.color = "#000000"; // Testo nero per contrasto
          }

          // Process each editable cell
          for (const field of [
            "raggruppamento1",
            "raggruppamento2",
            "raggruppamento3",
            "localCorporate",
            "userResponsible",
            "responsible",
          ]) {
            const cell = row.getCell(field);
            if (cell) {
              const cellElement = cell.getElement();

              // Verifica se questo campo specifico ha un valore pending
              const hasPendingValue =
                pendingValues && pendingValues[field] !== undefined;

              if (pendingChangeBy === user.email) {
                // If changes are from current user, highlight but DON'T disable
                cellElement.style.backgroundColor = hasPendingValue
                  ? "#E3F2FD"
                  : ""; // Light blue for own changes
                cellElement.style.color = hasPendingValue ? "#0D47A1" : ""; // Darker blue text
                cellElement.title = hasPendingValue
                  ? `Hai modificato questo valore da ${rowData[field]} a ${pendingValues[field]}`
                  : "Hai già inviato modifiche pendenti per questo record. Puoi continuare a modificarlo.";
              } else {
                // If changes are from another user, disable
                cellElement.style.backgroundColor = hasPendingValue
                  ? "#FFEBEE"
                  : ""; // Light red for others' changes
                cellElement.style.color = hasPendingValue ? "#B71C1C" : ""; // Darker red text
                cellElement.title = hasPendingValue
                  ? `${pendingChangeBy} ha modificato questo valore da ${rowData[field]} a ${pendingValues[field]}`
                  : `Questo record ha modifiche pendenti inviate da ${pendingChangeBy}`;
              }

              if (hasPendingValue) {
                cellElement.setAttribute(
                  "data-pending-value",
                  pendingValues[field]
                );
              }
              cellElement.setAttribute("data-modified-by", pendingChangeBy);
            }
          }

          // Forza il ridisegno per applicare il formatter che mostra i valori pending
          row.reformat();
        } else if (!isRecentlyApproved) {
          // Reimposta se il record non ha più modifiche pendenti E non è approvato
          row.update(
            {
              hasPendingChanges: false,
              pendingChangeBy: null,
              pendingValues: null,
            },
            true
          );

          // Rimuovi gli stili
          const rowElement = row.getElement();
          rowElement.style.backgroundColor = ""; // Reset background
          rowElement.removeAttribute("data-modified-by");
          rowElement.classList.remove("recently-approved-row");
          rowElement.style.borderLeft = "";
          rowElement.removeAttribute("data-approved");

          for (const field of [
            "raggruppamento1",
            "raggruppamento2",
            "raggruppamento3",
            "localCorporate",
            "userResponsible",
            "responsible",
          ]) {
            const cell = row.getCell(field);
            if (cell) {
              const cellElement = cell.getElement();
              cellElement.style.backgroundColor = ""; // Reset background
              cellElement.style.color = ""; // Reset text color
              cellElement.style.border = ""; // Reset border
              cellElement.title = "";
              cellElement.classList.remove("approved-field");
              cellElement.removeAttribute("data-modified-by");
              cellElement.removeAttribute("data-value");
              cellElement.removeAttribute("data-pending-value");
              cellElement.removeAttribute("aria-label");
            }
          }
        }
      });

      // Forza un ridisegno della tabella per assicurarsi che tutti i formatter siano applicati
      table.redraw(false);
    } catch (error) {
      console.error(
        "Errore nel verificare i record con modifiche pendenti:",
        error
      );
    }
  };

  const toggleSearchBox = () => {
    setSearchBoxVisible(!searchBoxVisible);
  };

  // Effect per caricare il ruolo utente all'avvio
  useEffect(() => {
    if (table) {
      const tableElement = table.element;
      tableElement.classList.add("hide-filters");
    }
  }, [table]);

  // Effect per caricare i dati di Level1Capex
  useEffect(() => {
    const loadLevel1Options = async () => {
      try {
        const data = await LevelCapexService.getLevels1();

        // Debug: visualizza i dati originali ricevuti dal servizio
        // Memorizza i dati completi e mantieni il valore localCorporate originale
        const completeData = data.map((item) => ({
          code: item.level1_code,
          desc: item.level1_desc,
          display: `${item.level1_code} - ${item.level1_desc}`,
          localCorporate: item.localCorporate, // Mantieni il valore originale
        }));

        // Debug: log dei valori di localCorporate presenti nei dati
        const uniqueLocalCorpValues = [
          ...new Set(completeData.map((item) => item.localCorporate)),
        ];

        setListValuesWithCodes(completeData);

        // Prepara le opzioni per il filtro dell'header
        const allOptions = data.map((item) => ({
          value: item.level1_desc,
          label: `${item.level1_code} - ${item.level1_desc}`,
        }));

        if (table) {
          const column = table.getColumn("raggruppamento1");
          if (column) {
            column.updateDefinition({
              headerFilterParams: {
                values: allOptions,
                displayField: "label",
                valueField: "value",
              },
            });
          }
        }
      } catch (error) {
        console.error("Error loading level 1 options:", error);
        showNotification("Error loading level 1 options", "error");
      }
    };

    loadLevel1Options();
  }, [table]);

  // Effect per il caricamento iniziale dei dati
  useEffect(() => {
    if (table) {
      fetchData().then(() => {
        // Call after data is loaded
        disableEditingForRecordsWithPendingChanges();
      });
    }
  }, [table]);

  // Call after pending changes count is updated
  useEffect(() => {
    if (table) {
      disableEditingForRecordsWithPendingChanges();
    }
  }, [pendingChangesCount]);

  // Aggiornamento del conteggio delle modifiche pendenti
  useEffect(() => {
    if (table && (bestRole === "Admin" || bestRole === "Validator")) {
      const updatePendingChangesCount = async () => {
        await loadPendingChangesCount();
      };

      updatePendingChangesCount();

      // Aggiorna il conteggio ogni minuto
      const intervalId = setInterval(updatePendingChangesCount, 60000);

      return () => clearInterval(intervalId);
    }
  }, [table, bestRole]);

  // Effetto per il setup della tabella
  useEffect(() => {
    if (tableRef.current && !table) {
      const resizeTable = () => {
        if (tableRef.current) {
          const height = tableRef.current.parentElement.clientHeight;
          tableRef.current.style.height = `${height}px`;
          if (table) {
            table.redraw(true);
          }
        }
      };

      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          resizeTable();
        });
      });

      if (tableRef.current) {
        resizeObserver.observe(tableRef.current.parentElement);
      }

      const newTable = new Tabulator(tableRef.current, {
        columns: columns,
        layout: "fitColumns",
        pagination: "local",
        paginationSize: 100,
        paginationSizeSelector: [10, 25, 50, 100],
        movableColumns: true,
        height: "100%",
        placeholder: "Nessun dato disponibile",
        initialSort: [{ column: "afeCode", dir: "desc" }],
        rowHeight: 40,
        selectable: true,
        selectableRangeMode: "click",
        selectablePersistence: true,
        responsiveLayout: false,
        autoResize: false,
        selectableRange: true,
        navigationMode: "cell",
        clipboard: true,
        editTriggerEvent: "dblclick",
        headerFilterLiveFilter: true,
        headerFilterPlaceholder: "Filter...",
        // Callback per la renderizzazione delle righe
        rowFormatter: function (row) {
          const rowData = row.getData();

          // Se il record è stato approvato, applicagli uno stile speciale
          if (rowData.recentlyApproved === true) {
            const rowElement = row.getElement();
            rowElement.classList.add("recently-approved-row");
            rowElement.style.backgroundColor = "#F1F8E9"; // Verde chiarissimo

            // Aggiungi attributo data-approved per facilitare il targeting CSS
            rowElement.setAttribute("data-approved", "true");

            const cells = rowElement.querySelectorAll(".tabulator-cell");
            if (cells.length > 0) {
              const selectionCell = cells[0];
              selectionCell.style.backgroundColor = "#2E7D32"; // Verde scuro
              selectionCell.style.color = "#FFFFFF"; // Testo bianco per migliorare contrasto
            }
          } else if (rowData.hasPendingChanges) {
            const rowElement = row.getElement();
            rowElement.style.backgroundColor = "#FFF8E1"; // Sfondo giallo chiaro

            const cells = rowElement.querySelectorAll(".tabulator-cell");
            if (cells.length > 0) {
              const selectionCell = cells[0]; // La prima cella è la colonna di selezione
              selectionCell.style.backgroundColor = "#ffd700"; // Giallo brillante
              selectionCell.style.color = "#000000"; // Testo nero per contrasto
            }
          }
        },
        // Callback per la renderizzazione delle celle
        cellFormatter: function (cell) {
          const rowData = cell.getRow().getData();
          const field = cell.getField();

          // Applica stili speciali solo alle colonne editabili
          if (
            [
              "raggruppamento1",
              "raggruppamento2",
              "raggruppamento3",
              "localCorporate",
              "userResponsible",
              "responsible",
            ].includes(field)
          ) {
            if (rowData.recentlyApproved === true) {
              const element = cell.getElement();

              if (bestRole === "Admin" || bestRole === "Validator") {
                // Per gli admin e validator, usa uno stile che indica che è approvato ma modificabile
                element.classList.add("approved-field-admin-editable");
                element.style.backgroundColor = "#E8F5E9"; // Verde chiaro
                element.style.color = "#2E7D32"; // Verde scuro
                element.style.border = "1px dashed #81C784"; // Bordo tratteggiato per indicare che è modificabile
                element.title = `Questo campo è stato approvato. Come ${bestRole}, puoi comunque modificarlo.`;
              } else {
                // Per gli altri utenti, mantieni lo stile originale
                element.classList.add("approved-field");
                element.style.backgroundColor = "#E8F5E9"; // Verde chiaro
                element.style.color = "#2E7D32"; // Verde scuro
                element.style.border = "1px solid #81C784"; // Bordo verde
                element.title =
                  "Questo campo è stato approvato e non può essere modificato";
              }

              // Assicurati che il valore sia visibile
              const value = cell.getValue();
              if (value !== null && value !== undefined) {
                element.setAttribute("data-value", value);
              }
            }
          }
        },
        // Callback dopo il caricamento dei dati
        dataLoaded: function (data) {
          // Controlla ogni riga per record approvati dopo il caricamento dei dati
          setTimeout(() => {
            disableEditingForRecordsWithPendingChanges();
            loadPendingValuesIntoRecords(); // Aggiungi qui il caricamento dei valori pending
          }, 100);
        },
        dataSorted: function () {
          // Riapplica gli stili quando l'ordinamento cambia
          setTimeout(() => {
            disableEditingForRecordsWithPendingChanges();
          }, 100);
        },
        dataFiltered: function () {
          // Riapplica gli stili quando il filtro cambia
          setTimeout(() => {
            disableEditingForRecordsWithPendingChanges();
          }, 100);
        },
        pageLoaded: function () {
          // Riapplica gli stili quando si cambia pagina
          setTimeout(() => {
            disableEditingForRecordsWithPendingChanges();
          }, 100);
        }
      }); 

      newTable.on("scrollVertical", function(top){
        setScrollTopPosition(top);
      });
      setTable(newTable);

      // Inizializzazione della visibilità delle colonne
      const initialVisibility = {};
      columns.forEach((col) => {
        if (col.columnId) {
          initialVisibility[col.columnId] = col.visible;
        }
      });
      setVisibleColumns(initialVisibility);

      return () => {
        if (newTable) {
          newTable.destroy();
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, []);

  useEffect(() => {
    if (table) {
      // Clean up previous handlers
      table.off("cellClick");
      table.off("cellDblClick");

      // When a cell is clicked
      table.on("cellClick", function (e, cell) {
        const rowData = cell.getRow().getData();
        const column = cell.getColumn().getDefinition();
        const field = cell.getField();

        // Only check for editable columns
        if (
          [
            "raggruppamento1",
            "raggruppamento2",
            "raggruppamento3",
            "localCorporate",
            "userResponsible",
            "responsible",
          ].includes(field)
        ) {
          // NUOVO: Blocca se il record è stato approvato (tranne per admin e validator)
          if (bestRole === "Coordinator") {
            e.stopPropagation();
            showNotification(
              "Il ruolo Coordinator può solo visualizzare i dati, non modificarli",
              "warning"
            );
            return false;
          }

          if (
            rowData.recentlyApproved === true &&
            bestRole !== "Admin" &&
            bestRole !== "Validator"
          ) {
            e.stopPropagation();
            showNotification(
              "Questo record è stato approvato e non può essere modificato",
              "warning"
            );
            return false;
          } else if (
            rowData.recentlyApproved === true &&
            (bestRole === "Admin" || bestRole === "Validator")
          ) {
            // Optional: notify admin/validator they're editing an approved record
            showNotification(
              `Stai modificando un record approvato (permesso speciale ${bestRole})`,
              "info"
            );
          }

          // Block if there are pending changes by others
          if (
            rowData.hasPendingChanges &&
            rowData.pendingChangeBy !== user.email
          ) {
            e.stopPropagation();
            showNotification(
              `Questo record ha modifiche pendenti inviate da ${rowData.pendingChangeBy}`,
              "warning"
            );
            return false;
          }
        }
      });

      // Double click handler (important for edit triggering)
      table.on("cellDblClick", function (e, cell) {
        const rowData = cell.getRow().getData();
        const field = cell.getField();

        // Only for editable columns
        if (
          [
            "raggruppamento1",
            "raggruppamento2",
            "raggruppamento3",
            "localCorporate",
            "userResponsible",
            "responsible",
          ].includes(field)
        ) {
          // NUOVO: Blocca se il record è stato approvato (tranne per admin e validator)
          if (bestRole === "Coordinator") {
            e.stopPropagation();
            showNotification(
              "Il ruolo Coordinator può solo visualizzare i dati, non modificarli",
              "warning"
            );
            return false;
          }
          if (
            rowData.recentlyApproved === true &&
            bestRole !== "Admin" &&
            bestRole !== "Validator"
          ) {
            e.stopPropagation();
            e.preventDefault(); // Prevent default double-click behavior
            showNotification(
              "Questo record è stato approvato e non può essere modificato",
              "warning"
            );
            return false;
          }

          // Block if there are pending changes by others
          if (
            rowData.hasPendingChanges &&
            rowData.pendingChangeBy !== user.email
          ) {
            e.stopPropagation();
            e.preventDefault(); // Prevent default double-click behavior
            showNotification(
              `Questo record ha modifiche pendenti inviate da ${rowData.pendingChangeBy}`,
              "warning"
            );
            return false;
          }

          // Controllo specifico per raggruppamento1: verifica se localCorporate è impostato
          if (field === "raggruppamento1" && !rowData.localCorporate) {
            e.stopPropagation();
            e.preventDefault(); // Previene il comportamento di doppio click
            showNotification(
              "È necessario selezionare prima un valore Local/Corporate per questo record",
              "warning"
            );
            return false;
          }
        }
      });

      return () => {
        if (table) {
          table.off("cellClick");
          table.off("cellDblClick");
        }
      };
    }
  }, [table, user?.email, bestRole]);

  // Add an explicit check in the cellEditing event
  useEffect(() => {
    if (table) {
      table.off("cellEditing");

      table.on("cellEditing", function (cell) {
        const rowData = cell.getRow().getData();
        const field = cell.getField();
        // Only for our editable columns
        if (
          [
            "raggruppamento1",
            "raggruppamento2",
            "raggruppamento3",
            "localCorporate",
            "userResponsible",
            "responsible",
          ].includes(field)
        ) {
          // NUOVO: Blocca se il record è stato approvato (tranne per admin e validator)
          if (bestRole === "Coordinator") {
            showNotification(
              "Il ruolo Coordinator può solo visualizzare i dati, non modificarli",
              "warning"
            );
            return false;
          }
          if (
            rowData.recentlyApproved === true &&
            bestRole !== "Admin" &&
            bestRole !== "Validator"
          ) {
            showNotification(
              "Questo record è stato approvato e non può essere modificato",
              "warning"
            );
            return false; // Blocca l'editing
          }

          // Block ONLY if there are pending changes from others
          if (
            rowData.hasPendingChanges &&
            rowData.pendingChangeBy !== user?.email
          ) {
            showNotification(
              `Questo record ha modifiche pendenti inviate da ${rowData.pendingChangeBy}`,
              "warning"
            );
            return false; // Blocks editing
          }

          // Controllo specifico per raggruppamento1
          if (field === "raggruppamento1" && !rowData.localCorporate) {
            showNotification(
              "È necessario selezionare prima un valore Local/Corporate per questo record",
              "warning"
            );
            return false; // Blocca l'editing
          }
        }

        // Allow editing otherwise
        return true;
      });

      return () => {
        if (table) {
          table.off("cellEditing");
        }
      };
    }
  }, [table, user?.email, bestRole]);

  // Gestione dell'evento cellEdited
  useEffect(() => {
    if (table) {
      table.off("cellEdited");

      table.on("cellEdited", async (cell) => {
        try {
          const row = cell.getRow();
          const rowData = row.getData();
          const oldValue = cell.getOldValue();
          const newValue = cell.getValue();
          const field = cell.getField();

          // NUOVO: Verifica se il record è stato approvato (tranne per admin e validator)
          if (
            rowData.recentlyApproved === true &&
            bestRole !== "Admin" &&
            bestRole !== "Validator"
          ) {
            showNotification(
              "Questo record è stato approvato e non può essere modificato",
              "error"
            );
            cell.setValue(oldValue); // Ripristina il valore originale
            return;
          }

          if (!rowData.id || rowData.id <= 0) {
            showNotification("Errore: ID AFE non valido", "error");
            cell.setValue(oldValue);
            return;
          }

          // Nel tuo handler cellEdited, modifica questa parte:
          try {
            const pendingChanges = await AfeService.getPendingRecordChanges();

            // Cerca modifiche pendenti per questo record FATTE DA ALTRI UTENTI
            const existingChanges = pendingChanges.filter(
              (change) =>
                (change.afeId === rowData.id ||
                  change.afeOriginalId === rowData.original_Id) &&
                change.status === "pending" &&
                change.modifiedBy !== user.email // Aggiungi questa condizione
            );

            if (existingChanges.length > 0) {
              // Mostriamo solo un messaggio per modifiche di altri utenti
              const latestChange = existingChanges.reduce((latest, current) => {
                return new Date(current.modifiedAt) >
                  new Date(latest.modifiedAt)
                  ? current
                  : latest;
              }, existingChanges[0]);

              showNotification(
                `Questo record ha modifiche pendenti inviate da ${latestChange.modifiedBy}`,
                "error"
              );
              cell.setValue(oldValue); // Ripristina il valore originale

              // Riaggiorna la visualizzazione delle modifiche pendenti
              await disableEditingForRecordsWithPendingChanges();
              return;
            }
          } catch (error) {
            console.error(
              "Errore nel controllare le modifiche pendenti:",
              error
            );
          }

          // NUOVO: Gestione speciale per il campo localCorporate
          if (field === "localCorporate") {
            // Reset dei campi di raggruppamento quando cambia localCorporate
            try {
              await row.update(
                {
                  raggruppamento1: "",
                  raggruppamento2: "",
                  raggruppamento3: "",
                },
                false
              );

              // Verifica che il valore selezionato corrisponda a qualche opzione in listValuesWithCodes
              const normalizedNewValue = normalizeValue(newValue);
              const matchingOptions = listValuesWithCodes.filter(
                (item) =>
                  normalizeValue(item.localCorporate) === normalizedNewValue
              );

              if (matchingOptions.length === 0) {
                // Se il valore non corrisponde a nessuna opzione, avvisa l'utente
                showNotification(
                  `Attenzione: Il valore '${newValue}' non corrisponde a nessuna opzione disponibile in Level1Capex`,
                  "warning"
                );
              } else {
                showNotification(
                  `I campi di raggruppamento sono stati reimpostati. Trovate ${matchingOptions.length} opzioni disponibili per '${newValue}'.`,
                  "info"
                );
              }
            } catch (error) {
              console.error(
                "Errore nel reset dei campi di raggruppamento:",
                error
              );
            }
          }

          // Gestione delle dipendenze a cascata per i raggruppamenti
          if (field === "raggruppamento1") {
            try {
              const level1Data = await LevelCapexService.getLevels1();
              const selectedLevel1 = level1Data.find(
                (item) => item.level1_desc === newValue
              );

              if (!selectedLevel1) {
                console.error("Nessun level1 trovato per:", newValue);
                return;
              }

              const level2Data = await LevelCapexService.getLevels2ByLevel1(
                selectedLevel1.level1_code
              );

              const values = level2Data.map((item) => ({
                value: item.level2_desc,
                label: `${item.level2_code} - ${item.level2_desc}`,
              }));

              cell
                .getTable()
                .getColumn("raggruppamento2")
                .updateDefinition({
                  editorParams: {
                    values,
                    displayField: "label",
                    valueField: "value",
                  },
                  headerFilterParams: {
                    values,
                    displayField: "label",
                    valueField: "value",
                  },
                });

              await row.update({
                raggruppamento2: "",
                raggruppamento3: "",
              });
            } catch (error) {
              console.error("Errore completo:", error);
            }
          } else if (field === "raggruppamento2") {
            try {
              // CORREZIONE: Ottieni il valore corrente di raggruppamento1 considerando anche i valori pending
              let raggruppamento1Value = rowData.raggruppamento1;
              if (
                rowData.pendingValues &&
                rowData.pendingValues.raggruppamento1 !== undefined
              ) {
                raggruppamento1Value = rowData.pendingValues.raggruppamento1;
              }

              if (!raggruppamento1Value) {
                console.error(
                  "Raggruppamento1 non impostato, impossibile caricare level2"
                );
                showNotification(
                  "È necessario selezionare prima un valore per Raggruppamento 1",
                  "warning"
                );
                cell.setValue(oldValue);
                return;
              }

              // Trova il level1 corrispondente
              const level1Data = await LevelCapexService.getLevels1();
              const selectedLevel1 = level1Data.find(
                (item) => item.level1_desc === raggruppamento1Value
              );

              if (!selectedLevel1) {
                console.error(
                  "Nessun level1 trovato per:",
                  raggruppamento1Value
                );
                showNotification(
                  "Errore: impossibile trovare il Raggruppamento 1 corrispondente",
                  "error"
                );
                cell.setValue(oldValue);
                return;
              }

              // Ottieni solo i level2 per questo level1
              const level2Data = await LevelCapexService.getLevels2ByLevel1(
                selectedLevel1.level1_code
              );

              // Cerca il level2 selezionato nella lista filtrata
              const selectedLevel2 = level2Data.find(
                (item) => item.level2_desc === newValue
              );

              if (!selectedLevel2) {
                console.error(
                  "Nessun level2 trovato per:",
                  newValue,
                  "in level1:",
                  raggruppamento1Value
                );
                showNotification(
                  `Errore: ${newValue} non è un'opzione valida per ${raggruppamento1Value}`,
                  "error"
                );
                cell.setValue(oldValue);
                return;
              }

              // Carica i level3 per questo level2
              const level3Data = await LevelCapexService.getLevels3ByLevel2(
                selectedLevel2.level2_code
              );

              const values = level3Data.map((item) => ({
                value: item.level3_desc,
                label: `${item.level3_code} - ${item.level3_desc}`,
              }));

              cell
                .getTable()
                .getColumn("raggruppamento3")
                .updateDefinition({
                  editorParams: {
                    values,
                    displayField: "label",
                    valueField: "value",
                  },
                  headerFilterParams: {
                    values,
                    displayField: "label",
                    valueField: "value",
                  },
                });

              await row.update({
                raggruppamento3: "",
              });

            } catch (error) {
              console.error("Errore completo in raggruppamento2:", error);
              showNotification(
                "Errore nel caricamento delle opzioni per Raggruppamento 3",
                "error"
              );
              cell.setValue(oldValue);
            }
          } else if (field === "raggruppamento3") {
            try {
              // CORREZIONE: Ottieni i valori correnti di raggruppamento1 e raggruppamento2 considerando i valori pending
              let raggruppamento1Value = rowData.raggruppamento1;
              let raggruppamento2Value = rowData.raggruppamento2;

              if (
                rowData.pendingValues &&
                rowData.pendingValues.raggruppamento1 !== undefined
              ) {
                raggruppamento1Value = rowData.pendingValues.raggruppamento1;
              }

              if (
                rowData.pendingValues &&
                rowData.pendingValues.raggruppamento2 !== undefined
              ) {
                raggruppamento2Value = rowData.pendingValues.raggruppamento2;
              }

              if (!raggruppamento1Value || !raggruppamento2Value) {
                console.error(
                  "Raggruppamento1 o raggruppamento2 non impostati"
                );
                showNotification(
                  "È necessario selezionare prima i valori per Raggruppamento 1 e 2",
                  "warning"
                );
                cell.setValue(oldValue);
                return;
              }

              // Trova il level1 corrispondente
              const level1Data = await LevelCapexService.getLevels1();
              const selectedLevel1 = level1Data.find(
                (item) => item.level1_desc === raggruppamento1Value
              );

              if (!selectedLevel1) {
                console.error(
                  "Nessun level1 trovato per:",
                  raggruppamento1Value
                );
                showNotification(
                  "Errore: impossibile trovare il Raggruppamento 1 corrispondente",
                  "error"
                );
                cell.setValue(oldValue);
                return;
              }

              // Ottieni i level2 per questo level1 e trova quello selezionato
              const level2Data = await LevelCapexService.getLevels2ByLevel1(
                selectedLevel1.level1_code
              );

              const selectedLevel2 = level2Data.find(
                (item) => item.level2_desc === raggruppamento2Value
              );

              if (!selectedLevel2) {
                console.error(
                  "Nessun level2 trovato per:",
                  raggruppamento2Value
                );
                showNotification(
                  "Errore: impossibile trovare il Raggruppamento 2 corrispondente",
                  "error"
                );
                cell.setValue(oldValue);
                return;
              }

              // Ottieni i level3 per questo level2 e verifica che il valore selezionato sia valido
              const level3Data = await LevelCapexService.getLevels3ByLevel2(
                selectedLevel2.level2_code
              );

              const selectedLevel3 = level3Data.find(
                (item) => item.level3_desc === newValue
              );

              if (!selectedLevel3) {
                console.error(
                  "Nessun level3 trovato per:",
                  newValue,
                  "in level2:",
                  raggruppamento2Value
                );
                showNotification(
                  `Errore: ${newValue} non è un'opzione valida per ${raggruppamento2Value}`,
                  "error"
                );
                cell.setValue(oldValue);
                return;
              }

            } catch (error) {
              console.error("Errore completo in raggruppamento3:", error);
              showNotification(
                "Errore nella validazione di Raggruppamento 3",
                "error"
              );
              cell.setValue(oldValue);
            }
          }

          // NUOVO: Utilizziamo i dati originali che abbiamo memorizzato durante il caricamento
          // Recupera il record originale dal nostro stato
          const trueOriginalData = originalTableData[rowData.id];

          if (!trueOriginalData) {
            console.error(
              `Nessun dato originale trovato per il record ID: ${rowData.id}`
            );
            showNotification("Errore: dati originali mancanti", "error");
            cell.setValue(oldValue);
            return;
          }

          // NUOVO: Otteniamo le modifiche pendenti esistenti per questo record
          try {
            const pendingChanges = await AfeService.getPendingRecordChanges();
            const existingChange = pendingChanges.find(
              (change) =>
                (change.afeId === rowData.id ||
                  change.afeOriginalId === rowData.original_Id) &&
                change.status === "pending" &&
                change.modifiedBy === user.email
            );

            // Crea modifiedData partendo dai dati originali
            const modifiedData = { ...trueOriginalData };

            // Se ci sono modifiche pendenti esistenti, applicale prima di aggiungere la nuova modifica
            if (existingChange) {
              try {
                const pendingModifiedRecord = JSON.parse(
                  existingChange.modifiedRecordJson
                );

                // Trova i campi modificati e applicali a modifiedData
                Object.keys(pendingModifiedRecord).forEach((key) => {
                  // Ignora proprietà speciali di gestione
                  if (
                    key === "pendingValues" ||
                    key === "pendingChangeBy" ||
                    key === "hasPendingChanges"
                  ) {
                    return;
                  }

                  if (
                    JSON.stringify(pendingModifiedRecord[key]) !==
                    JSON.stringify(trueOriginalData[key])
                  ) {
                    modifiedData[key] = pendingModifiedRecord[key];
                  }
                });
              } catch (error) {
                console.error(
                  "Errore nel parsing delle modifiche esistenti:",
                  error
                );
              }
            }

            // Poi applica la modifica corrente
            modifiedData[field] = newValue;

            // Rimuovi le proprietà di gestione per sicurezza
            delete modifiedData.pendingValues;
            delete modifiedData.pendingChangeBy;
            delete modifiedData.hasPendingChanges;

            // Normalizza i valori per i campi editabili per garantire consistenza
            [
              "localCorporate",
              "userResponsible",
              "responsible",
              "raggruppamento1",
              "raggruppamento2",
              "raggruppamento3",
            ].forEach((key) => {
              if (
                trueOriginalData[key] === null ||
                trueOriginalData[key] === undefined
              ) {
                trueOriginalData[key] = "";
              }

              if (
                modifiedData[key] === null ||
                modifiedData[key] === undefined
              ) {
                modifiedData[key] = "";
              }
            });

            // Aggiorna anche il valore pending localmente per la visualizzazione immediata
            // Prima ottieni tutte le modifiche pendenti esistenti
            const existingPendingValues = { ...(rowData.pendingValues || {}) };
            existingPendingValues[field] = newValue;

            row.update({
              ...rowData,
              pendingValues: existingPendingValues,
              pendingChangeBy: user.email,
              hasPendingChanges: true,
            });

            // Invia la modifica pendente al server
            try {
              // Prepara il payload con i dati originali e modificati
              const payload = {
                afeId: rowData.id,
                afeCode: String(rowData.afeCode),
                afeOriginalId: rowData.original_Id,
                originalRecordJson: JSON.stringify(trueOriginalData),
                modifiedRecordJson: JSON.stringify(modifiedData),
                modifiedBy: user.email,
              };
              await AfeService.updatePendingRecordChange(payload);
              await loadPendingChangesCount(); // Aggiorna il conteggio dopo l'aggiunta

              // Forza il ridisegno della cella per mostrare il valore pending
              if(document.getElementsByClassName("tabulator-tableholder").length > 0 && scrollTopPosition > 0) {
                document.getElementsByClassName("tabulator-tableholder")[0].scrollTop = scrollTopPosition;
              }
              cell.getRow().reformat();
              showNotification(
                "Modifica salvata e in attesa di approvazione",
                "info"
              );
            } catch (error) {
              console.error("Error submitting pending change:", error);
              cell.setValue(oldValue);

              if (error.response?.data?.detail) {
                showNotification(
                  `Errore: ${error.response.data.detail}`,
                  "error"
                );
              } else {
                showNotification(
                  "Errore nell'invio della modifica pendente",
                  "error"
                );
              }
            }
          } catch (error) {
            console.error(
              "Errore nel recuperare le modifiche pendenti:",
              error
            );
            showNotification(
              "Errore nel recuperare le modifiche pendenti",
              "error"
            );
            cell.setValue(oldValue);
            return;
          }
        } catch (error) {
          console.error("Error handling edit:", error);
          //cell.setValue(oldValue);

          if (error.response?.data?.detail) {
            showNotification(`Errore: ${error.response.data.detail}`, "error");
          } else {
            showNotification("Errore nella gestione della modifica", "error");
          }
        }
      });
    }
  }, [table, user.email, originalTableData, bestRole, listValuesWithCodes, scrollTopPosition]);

  const updateHeight = (filtersActive) => {
    if (document.getElementsByClassName("CAPEX_container").length > 0) {
      let height_ = document.getElementsByClassName("CAPEX_container")[0].clientHeight;
      if(filtersActive) {
        height_ += 20; // Aggiungo spazio extra se i filtri sono attivi
      }
      setHeight(height_);
    }
  };
  
  useEffect(() => { 
    // Aggiungo l’event listener al resize della finestra
    window.addEventListener("resize", updateHeight);

    document.body.classList.add("noOverflowVertical");
    window.scrollTo(0, 0);
    // Cleanup per evitare memory leaks
    return () => {
      window.removeEventListener("resize", updateHeight);
      document.body.classList.remove("noOverflowVertical");
    };
  }, []);  

  useEffect(() => {
    if(!loadingFetch){
      updateHeight(filtersActive);
    }
  }, [searchBoxVisible, filtersActive, loadingFetch]);

  useEffect(() => {
    if (tableRef.current && table && height > 0) {
      table.setHeight(`calc(100vh - ${height + 110}px)`);
    }
  }, [table, height]);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <div className="CAPEX_container">
        <Q8Breadcrumbs
          links={[{ label: "CAPEX", href: "/capex" }]}
          variant="primary"
        /> 
        <Box
          sx={{
            mt: 1,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h4" sx={{ color: "primary.main" }}>
              Tabella CAPEX
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {/* 1. Colonne Visibili */}
            <Q8Button
              variant="secondary"
              label="Colonne Visibili"
              icon={<ColumnIcon />}
              onClick={() => setColumnSelectorOpen(true)}
              size="small"
            />

            {/* 2. Mostra Ricerca */}
            <Q8Button
              variant="secondary"
              label={searchBoxVisible ? "Nascondi Ricerca" : "Mostra Ricerca"}
              icon={<SearchIcon />}
              onClick={toggleSearchBox}
              size="small"
            />

            {/* 3. Mostra Filtri */}
            <Q8Button
              variant="tertiary"
              label={filtersActive ? "Nascondi Filtri" : "Mostra Filtri"}
              icon={<FilterIcon />}
              onClick={() => {
                if (table) {
                  const newState = !filtersActive;
                  setFiltersActive(newState);

                  const tableElement = table.element;
                  if (newState) {
                    tableElement.classList.remove("hide-filters");
                  } else {
                    tableElement.classList.add("hide-filters");
                    table.getColumns().forEach((column) => {
                      if (column.getField() && column.getHeaderFilterValue()) {
                        column.setHeaderFilterValue("");
                      }
                    });
                  }
                }
              }}
              size="small"
            />

            {/* 4. Scarica Excel */}
            <Q8Button
              variant="secondary"
              label="Esporta Excel"
              icon={<ExportIcon />}
              onClick={handleDownloadExcel}
              size="small"
            />

            {/* Pulsanti visibili solo per Admin/Validator */}
            {(bestRole === "Admin" || bestRole === "Validator") && (
              <>
                {/* 5. Approva Record Selezionati */}
                <Q8Button
                  variant="secondary"
                  label="Approva Record Selezionati"
                  icon={<ApproveIcon />}
                  onClick={handleApproveSelectedRecords}
                  size="small"
                />

                {/* 6. Modifiche Pendenti */}
                <Q8Button
                  variant="secondary"
                  label={`Modifiche Pendenti (${pendingChangesCount})`}
                  onClick={handlePendingChangesClick}
                  size="small"
                />

                {/* 7. Storico Modifiche */}
                <Q8Button
                  variant="secondary"
                  label="Storico Modifiche"
                  icon={<HistoryIcon />}
                  onClick={() => navigate("/capex/history")}
                  size="small"
                />
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            my: 0.5,
            p: 0.5,
            bgcolor: "#FFF8E1",
            border: "1px solid #FFE082",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <InfoOutlined sx={{ color: "#FB8C00" }} />
          <Typography variant="body2">
            Per modificare un record, scegliere una colonna modificabile (colonne
            in giallo) ed effettuare un doppio click sulla cella per effettuare
            una modifica.
          </Typography>
        </Box>

        {(bestRole === "Admin" || bestRole === "Validator") && (
          <Box
            sx={{
              my: 0.5,
              p: 0.5,
              bgcolor: "#E8EAF6",
              border: "1px solid #9FA8DA",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <InfoOutlined sx={{ color: "#3F51B5" }} />
            <Typography variant="body2">
              <strong>Modalità {bestRole}:</strong> In qualità di{" "}
              {bestRole.toLowerCase()}, puoi modificare anche i record già
              approvati. I record approvati modificabili sono evidenziati con un
              bordo tratteggiato verde.
            </Typography>
          </Box>
        )}

        {/* Search box con larghezza limitata */}
        {searchBoxVisible && (
          <Box
            sx={{
              width: { xs: "100%", sm: "500px", md: "1000px" },
              maxWidth: "100%",
            }}
          >
            <AfeSearchBox onSearch={handleSearch} />
          </Box>
        )}
      </div>

      <div className="table_container">
        <div ref={tableRef} className="custom_table"></div>
      </div>

      <ChangeHistoryDialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        history={changeHistory || []}
        filters={historyFilters}
        onFiltersChange={setHistoryFilters}
      />

      <ColumnSelectorDialog
        open={columnSelectorOpen}
        onClose={() => setColumnSelectorOpen(false)}
        columns={columns}
        visibleColumns={visibleColumns}
        onVisibilityChange={handleColumnVisibilityChange}
      />

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Conferma Approvazione</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Stai per approvare {selectedRecordsCount} record. Questa azione non
            può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={() => setConfirmDialogOpen(false)}
          />
          <Q8Button
            variant="primary"
            label="Conferma"
            onClick={confirmApproval}
          />
        </DialogActions>
      </Dialog>

      <Q8SnackBar
        open={snackbar.open}
        type={snackbar.type}
        title="Notification"
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default AfeTable;
